import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  fetchSystems,
  fetchTenantsOverview,
  moveSystemToTenant,
  removeSystemFromTenant,
} from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { formatDate, formatDateTime } from "@/lib/format";
import type { SystemSummary, TenantOverview } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

function SystemRow({
  system,
  tenant,
  tenants,
  onDone,
}: {
  system: SystemSummary;
  tenant: TenantOverview;
  tenants: TenantOverview[];
  onDone: () => void;
}): JSX.Element {
  const [moveTarget, setMoveTarget] = useState("");
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () => removeSystemFromTenant(tenant.id, system.pws_id),
    onSuccess: onDone,
    onError: (e) => setError(e instanceof ApiError ? e.message : "Remove failed"),
  });
  const move = useMutation({
    mutationFn: () => moveSystemToTenant(tenant.id, system.pws_id, moveTarget),
    onSuccess: onDone,
    onError: (e) => setError(e instanceof ApiError ? e.message : "Move failed"),
  });

  function onRemove(): void {
    // eslint-disable-next-line no-alert
    if (
      window.confirm(
        `Remove ${system.pws_name} (${system.pws_id}) from ${tenant.name}?\n\n` +
          "The tenant loses access immediately. Local attributes and schedule " +
          "overrides are kept, so re-onboarding restores them.",
      )
    ) {
      remove.mutate();
    }
  }

  function onMove(): void {
    const target = tenants.find((t) => t.id === moveTarget);
    if (!target) return;
    // eslint-disable-next-line no-alert
    if (
      window.confirm(
        `Move ${system.pws_name} (${system.pws_id}) from ${tenant.name} to ${target.name}?`,
      )
    ) {
      move.mutate();
    }
  }

  const busy = remove.isPending || move.isPending;
  return (
    <li className="flex items-center gap-3 py-1.5 flex-wrap">
      <Link
        to={`/systems/${system.pws_id}`}
        className="text-sm text-ink-100 hover:underline"
      >
        {system.pws_name}
      </Link>
      <span className="text-xs text-ink-400 font-mono">{system.pws_id}</span>
      <span className="ml-auto inline-flex items-center gap-2">
        <select
          className="input w-auto py-0.5 text-xs"
          value={moveTarget}
          onChange={(e) => setMoveTarget(e.target.value)}
          aria-label={`Move ${system.pws_name} to another tenant`}
        >
          <option value="">Move to…</option>
          {tenants
            .filter((t) => t.id !== tenant.id)
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
        </select>
        <button
          type="button"
          className="btn-primary py-0.5 px-2 text-xs"
          disabled={!moveTarget || busy}
          onClick={onMove}
        >
          Move
        </button>
        <button
          type="button"
          className="btn-primary py-0.5 px-2 text-xs"
          disabled={busy}
          onClick={onRemove}
        >
          Remove
        </button>
      </span>
      {error && <span className="w-full text-xs text-severity-high">{error}</span>}
    </li>
  );
}

export function TenantsAdmin(): JSX.Element {
  usePageTitle("Tenants");
  const queryClient = useQueryClient();
  const overview = useQuery({ queryKey: ["tenants-overview"], queryFn: fetchTenantsOverview });
  const systems = useQuery({ queryKey: ["systems"], queryFn: fetchSystems });
  const [expanded, setExpanded] = useState<string | null>(null);

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ["tenants-overview"] });
    void queryClient.invalidateQueries({ queryKey: ["systems"] });
  }

  const byTenant = new Map<string, SystemSummary[]>();
  for (const s of systems.data ?? []) {
    if (!s.tenant_id) continue;
    const list = byTenant.get(s.tenant_id) ?? [];
    list.push(s);
    byTenant.set(s.tenant_id, list);
  }

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold inline-flex items-center">
          Tenants
          <InfoTip
            label="Tenants"
            body="Every customer organization: how many systems and active users it has and when someone last acted. Expand a row to remove a system or move it to a different tenant (e.g. a new consulting firm takes over)."
          />
        </h1>
        <Link to="/admin/onboard" className="text-sm text-ink-300 hover:text-ink-100">
          Add a PWS →
        </Link>
      </header>

      <section className="card overflow-x-auto">
        {overview.isLoading ? (
          <div className="skeleton h-40" aria-label="Loading tenants" />
        ) : overview.isError ? (
          <p className="text-sm text-severity-high">Failed to load tenants.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-ink-300 border-b border-ink-700">
              <tr>
                <th className="py-2 pr-4 font-medium">Tenant</th>
                <th className="py-2 pr-4 font-medium">Systems</th>
                <th className="py-2 pr-4 font-medium">Active users</th>
                <th className="py-2 pr-4 font-medium">Last activity</th>
                <th className="py-2 pr-4 font-medium">Created</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-700">
              {(overview.data ?? []).map((t) => (
                <Fragment key={t.id}>
                  <tr>
                    <td className="py-2 pr-4">
                      <span className="text-ink-100">{t.name}</span>
                      <span className="ml-2 text-xs text-ink-400 font-mono">{t.slug}</span>
                    </td>
                    <td className="py-2 pr-4">{t.system_count}</td>
                    <td className="py-2 pr-4">{t.user_count}</td>
                    <td className="py-2 pr-4 text-ink-300">
                      {t.last_activity ? formatDateTime(t.last_activity) : "—"}
                    </td>
                    <td className="py-2 pr-4 text-ink-300">{formatDate(t.created_at)}</td>
                    <td className="py-2 text-right">
                      {t.system_count > 0 && (
                        <button
                          type="button"
                          className="text-ink-300 hover:text-ink-100 text-xs"
                          onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                          aria-expanded={expanded === t.id}
                        >
                          {expanded === t.id ? "Hide systems ▴" : "Manage systems ▾"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === t.id && (
                    <tr>
                      <td colSpan={6} className="py-2 pl-4 bg-ink-700/30">
                        <ul className="divide-y divide-ink-700">
                          {(byTenant.get(t.id) ?? []).map((s) => (
                            <SystemRow
                              key={s.pws_id}
                              system={s}
                              tenant={t}
                              tenants={overview.data ?? []}
                              onDone={refresh}
                            />
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
